'use client';
import React, { useState } from 'react';
import QueryForm, { QueryFormData } from './QueryForm';
import MatchResults from './MatchResults';
import { ExpertCardData } from '@/components/ui/ExpertCard';
import { BadgeTier } from '@/components/ui/BadgeTierChip';
import { VerificationStatus } from '@/components/ui/VerificationBadge';

// Backend integration: POST /api/match-query returns top 5 experts
const mockMatchQuery = async (data: QueryFormData): Promise<ExpertCardData[]> => {
  await new Promise((r) => setTimeout(r, 1800));

  const allExperts: ExpertCardData[] = [
    { id: 'expert-seed-001', pseudonym: 'Advisor #K9M', avatarSeed: 'Advisor #K9M', headline: 'Senior brand strategy and go-to-market leader with two decades of FMCG portfolio scaling across South Asia and Southeast Asia.', domainTags: ['FMCG', 'Go-to-Market', 'Brand Strategy', 'Portfolio Management'], badgeTier: 'GOLD' as BadgeTier, averageRating: 4.4, ratingCount: 18, hourlyRate: 22000, verificationStatus: 'WEB_CHECKED' as VerificationStatus, experienceTier: 'PRINCIPAL' },
    { id: 'expert-009', pseudonym: 'Advisor #D3V', avatarSeed: 'Advisor #D3V', headline: 'Consumer internet and D2C growth specialist with a track record of scaling digital-first brands from early traction to category leadership.', domainTags: ['D2C', 'Consumer Internet', 'Growth Strategy', 'Digital Marketing'], badgeTier: 'GOLD' as BadgeTier, averageRating: 4.5, ratingCount: 15, hourlyRate: 28000, verificationStatus: 'WEB_CHECKED' as VerificationStatus, experienceTier: 'PRINCIPAL' },
    { id: 'expert-006', pseudonym: 'Advisor #F5K', avatarSeed: 'Advisor #F5K', headline: 'Private equity and portfolio operations specialist with focus on value creation, operational due diligence, and post-acquisition integration.', domainTags: ['Private Equity', 'Portfolio Operations', 'Due Diligence', 'Value Creation'], badgeTier: 'VAULT' as BadgeTier, averageRating: 4.8, ratingCount: 34, hourlyRate: 65000, verificationStatus: 'BUYER_VERIFIED' as VerificationStatus, experienceTier: 'EXPERT' },
    { id: 'expert-seed-002', pseudonym: 'Advisor #R3T', avatarSeed: 'Advisor #R3T', headline: 'M&A and financial due diligence specialist with deep experience across financial services, insurance, and capital markets transactions.', domainTags: ['M&A', 'Due Diligence', 'Financial Services', 'Capital Markets'], badgeTier: 'SILVER' as BadgeTier, averageRating: 4.1, ratingCount: 7, hourlyRate: 35000, verificationStatus: 'WEB_CHECKED' as VerificationStatus, experienceTier: 'EXPERT' },
    { id: 'expert-seed-003', pseudonym: 'Advisor #B6F', avatarSeed: 'Advisor #B6F', headline: 'Manufacturing and supply chain specialist focused on lean operations, cost reduction, and operational resilience.', domainTags: ['Manufacturing', 'Supply Chain', 'Lean Operations'], badgeTier: 'BRONZE' as BadgeTier, averageRating: 3.7, ratingCount: 2, hourlyRate: 14000, verificationStatus: 'PENDING' as VerificationStatus, experienceTier: 'SENIOR' },
  ];

  return allExperts
    .filter((e) => {
      if (!data.domain) return true;
      return e.domainTags.some((t) => t.toLowerCase().includes(data.domain.toLowerCase()));
    })
    .sort((a, b) => b.averageRating - a.averageRating)
    .slice(0, 5);
};

export default function NewQueryContent() {
  const [matchedExperts, setMatchedExperts] = useState<ExpertCardData[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [submittedQuery, setSubmittedQuery] = useState<QueryFormData | null>(null);

  const handleSubmit = async (data: QueryFormData) => {
    setLoading(true);
    setMatchedExperts(null);
    setSubmittedQuery(data);
    const results = await mockMatchQuery(data);
    setMatchedExperts(results);
    setLoading(false);
  };

  return (
    <div className="py-8">
      <div className="mb-8">
        <p className="text-xs font-mono-data text-primary uppercase tracking-widest mb-2">Buyer Query</p>
        <h1 className="font-heading text-display text-foreground mb-2">Post a Query</h1>
        <p className="text-muted-foreground text-sm max-w-lg">
          Describe your need. Our matching engine will surface the top-ranked experts from the live network.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
        <QueryForm onSubmit={handleSubmit} loading={loading} />
        <MatchResults
          experts={matchedExperts}
          loading={loading}
          query={submittedQuery}
        />
      </div>
    </div>
  );
}